import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {Client} from '../../client';
import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {
  AlgemeneInformatie,
  CasaClementina,
  ClientFiche,
  ClientFicheDienst,
  ContactpersoonNoodgeval,
  DienstenChequesType,
  FinancieleOptie,
  Knelpunten,
  MogelijkeProblematieken,
  Takenpakket,
  TakenpakketCategorie,
  TakenpakketCategorieStatus,
  TransportOptieNaarCasaClementina
} from '../../client-fiche';
import moment from 'moment';
import * as lodash from 'lodash';
import {dateInThePastValidator} from '../../../core/validation/form-validation';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {ClientFichePdfService} from "./client-fiche-pdf.service";

pdfMake.vfs = pdfFonts.vfs;

const dateFormat = 'DD/MM/YYYY';

@Component({
  selector: 'zah-client-fiche-beheer',
  templateUrl: './client-fiche-beheer.component.html',
  styleUrls: ['./client-fiche-beheer.component.scss'],
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: dateFormat,
        },
        display: {
          dateInput: dateFormat,
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        }
      }
    },
  ],
  standalone: false
})
export class ClientFicheBeheerComponent implements OnInit {
  @Input()
  client?: Client;
  @Output()
  cancelClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  okClicked: EventEmitter<Client> = new EventEmitter<Client>();

  private clientFichePdfService = inject(ClientFichePdfService);

  ClientFicheBeheerForm: FormGroup;

  dienstenchequesTypes: Array<DienstenChequesType> = [DienstenChequesType.ELEKTRONISCH, DienstenChequesType.PAPIER];
  takenpakketCategorieStatus: Array<TakenpakketCategorieStatus> = [TakenpakketCategorieStatus.OPGESTART, TakenpakketCategorieStatus.AANGEVRAAGD_MAAR_NOG_NIET_OPGESTART, TakenpakketCategorieStatus.NIET_NODIG];
  transportOptiesNaarCasaClementina: Array<TransportOptieNaarCasaClementina> = [TransportOptieNaarCasaClementina.ZELFSTANDIG, TransportOptieNaarCasaClementina.OPENBAAR_VERVOER_ZELFSTANDIG, TransportOptieNaarCasaClementina.OPENBAAR_VERVOER_MET_ONDERSTEUNING, TransportOptieNaarCasaClementina.ENKEL_MET_MEDEWERKER_ZORG_LEUVEN, TransportOptieNaarCasaClementina.OPGEHAALD_DOOR_BUSJE, TransportOptieNaarCasaClementina.GERAAKT_ER_NIET, TransportOptieNaarCasaClementina.ANDERE,]
  financieleOptiesCasaClementina: Array<FinancieleOptie> = [FinancieleOptie.BEWINDVOERDER, FinancieleOptie.VIA_FACTUUR, FinancieleOptie.CLIENT_MET_BANKKAART]

  ngOnInit(): void {
    this.ClientFicheBeheerForm = new FormGroup({
      dienstencheques: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getDienstencheques()),
      gebruikersnummer: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getGebruikersnummer(), Validators.pattern('^[0-9]*$')),
      geboortedatum: new FormControl(this.getInitialClientFicheGeboortedatum(), dateInThePastValidator(dateFormat)),
      moedertaal: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getMoedertaal()),
      conversatieTaal: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getConversatieTaal()),
      keyLock: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getKeyLock()),
      autoStaanPlaats: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getAutoStaanPlaats()),
      extraInfoBereikbaarheid: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getExtraInfoBereikbaarheid()),
      inwonendeGezinsleden: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getInwonendeGezinsleden()),
      socialeSituatieSchets: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getSocialeSituatieSchets()),
      extraInformatie: new FormControl(this.client?.getFiche()?.getAlgemeneInformatie()?.getExtraInformatie()),
      redenOpstart: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getRedenOpstart()),
      teBehalenDoelstellingen: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getTeBehalenDoelstellingen()),
      poetsdienstStatus: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getPoetsdienst()?.getStatus()),
      poetsdienstFrequentie: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getPoetsdienst()?.getFrequentie()),
      poetsdienstVasteMedewerkers: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getPoetsdienst()?.getVasteMedewerkers()),
      poetsdienstBelangrijksteTaken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getPoetsdienst()?.getBelangrijksteTaken()),
      gezinszorgStatus: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getGezinszorg()?.getStatus()),
      gezinszorgFrequentie: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getGezinszorg()?.getFrequentie()),
      gezinszorgVasteMedewerkers: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getGezinszorg()?.getVasteMedewerkers()),
      gezinszorgBelangrijksteTaken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getGezinszorg()?.getBelangrijksteTaken()),
      gezinszorgSpecifiekeInformatieVoorWeekend: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getGezinszorg()?.getSpecifiekeInformatieVoorWeekend()),
      maaltijdenAanHuisStatus: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getMaaltijdenAanHuis()?.getStatus()),
      maaltijdenAanHuisFrequentie: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getMaaltijdenAanHuis()?.getFrequentie()),
      maaltijdenAanHuisBelangrijksteTaken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getMaaltijdenAanHuis()?.getBelangrijksteTaken()),
      klusjesdienstStatus: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getKlusjesdienst()?.getStatus()),
      klusjesdienstBelangrijksteTaken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getKlusjesdienst()?.getBelangrijksteTaken()),
      psycholoogStatus: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getPsycholoog()?.getStatus()),
      psycholoogBelangrijksteTaken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getPsycholoog()?.getBelangrijksteTaken()),
      ergotherapeutStatus: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getErgotherapeut()?.getStatus()),
      ergotherapeutBelangrijksteTaken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getErgotherapeut()?.getBelangrijksteTaken()),
      mantelzorgcoordinatorStatus: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getMantelzorgcoordinator()?.getStatus()),
      mantelzorgcoordinatorBelangrijksteTaken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getMantelzorgcoordinator()?.getBelangrijksteTaken()),
      casemanagerStatus: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getCasemanager()?.getStatus()),
      casemanagerBelangrijksteTaken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getCasemanager()?.getBelangrijksteTaken()),
      casaClementinaContactpersoonNoodgevalNaam: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getCasaClementina()?.getContactpersoonNoodgeval()?.getNaam()),
      casaClementinaContactpersoonNoodgevalRelatieTotHulpvrager: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getCasaClementina()?.getContactpersoonNoodgeval()?.getRelatieTotHulpvrager()),
      casaClementinaContactpersoonNoodgevalTelefoonnummer: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getCasaClementina()?.getContactpersoonNoodgeval()?.getTelefoonnummer()),
      casaClementinaAandachtspuntenEnGemaakteAfspraken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getCasaClementina()?.getAandachtspuntenEnGemaakteAfspraken()),
      casaClementinaTransportOptiesNaarCasaClementina: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getCasaClementina()?.getTransportOptiesNaarCasaClementina()),
      casaClementinaVerduidelijkingAndereTransportOptieNaarCasaClementina: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getCasaClementina()?.getVerduidelijkingAndereTransportOptieNaarCasaClementina()),
      casaClementinaFinancieleOpties: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getCasaClementina()?.getFinancieleOpties()),
      stofzuigerWaarTeVinden: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getStofzuigerWaarTeVinden()),
      poetsproductenWaarTeVinden: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getPoetsproductenWaarTeVinden()),
      doekenVoddenDweilenWaarTeVinden: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getDoekenVoddenDweilenWaarTeVinden()),
      telescopischeSteelWaarTeVinden: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getTelescopischeSteelWaarTeVinden()),
      andereWaarTeVinden: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getAndereWaarTeVinden()),
      aandachtspuntenPoetsen: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getAandachtspuntenPoetsen()),
      aandachtspuntenAndereTaken: new FormControl(this.client?.getFiche()?.getTakenpakket()?.getAandachtspuntenAndereTaken()),
      allergieen: new FormControl(this.client?.getFiche()?.getKnelpunten()?.getAllergieen()),
      huisdieren: new FormControl(this.client?.getFiche()?.getKnelpunten()?.getHuisdieren()),
      roken: new FormControl(this.client?.getFiche()?.getKnelpunten()?.getRoken()),
      fysiekeProblematieken: new FormControl(this.client?.getFiche()?.getMogelijkeProblematieken()?.getFysiek()),
      mentaleProblematieken: new FormControl(this.client?.getFiche()?.getMogelijkeProblematieken()?.getMentaal()),
      financieleProblematieken: new FormControl(this.client?.getFiche()?.getMogelijkeProblematieken()?.getFinancieel()),
      andereProblematieken: new FormControl(this.client?.getFiche()?.getMogelijkeProblematieken()?.getAndere()),
      handvatenOmMetClientOmTeGaan: new FormControl(this.client?.getFiche()?.getMogelijkeProblematieken()?.getHandvatenOmMetClientOmTeGaan()),
      afsprakennota: new FormControl(this.client?.getFiche()?.getMogelijkeProblematieken()?.getAfsprakennota()),
      andereDiensten: new FormArray(lodash.concat(this.getInitialClientFicheAndereDiensten(), this.getEmptyClientFicheDienstForm())),
    });
    this.onAndereDienstenChanges();
    this.setFormState(this.client)
  }

  onDeleteAndereDienstClick(index: number) {
    this.andereDiensten.removeAt(index);
  }

  onCancelClicked(): void {
    this.cancelClicked.emit();
  }

  onOkClicked(): void {
    const clientFicheUpdate = new ClientFiche(
      this.createAndereDiensten(),
      new Date(),
      this.createAlgemeneInformatie(),
      this.createTakenpakket(),
      this.createKnelpunten(),
      this.createMogelijkeProblematieken(),
    );
    this.okClicked.emit(
      new Client(
        this.client.getId(),
        this.client.getVoornaam(),
        this.client.getAchternaam(),
        this.client.getNummer(),
        this.client.getAdres(),
        this.client.getDiensten(),
        this.client.getArchiveringsTijdstip(),
        clientFicheUpdate,
      ));
  }

  protected generateVolledigePDF() {
    const pdfTitel = 'Cliëntfiche van ' + this.client.getNaam();
    const pdf = this.clientFichePdfService.generateVolledigeClientFichePDF(pdfTitel, this.client.getFiche());
    pdfMake.createPdf(pdf).open();
  }

  protected generateClientGerichtePDF() {
    const pdfTitel = 'Cliëntfiche van ' + this.client.getNaam();
    const pdf = this.clientFichePdfService.generateClientGerichtePDF(pdfTitel, this.client.getFiche());
    pdfMake.createPdf(pdf).open();
  }

  private createAndereDiensten(): Array<ClientFicheDienst> {
    return this.andereDiensten.value.map(andereDienst => new ClientFicheDienst(
      this.getTrimmedOrUndefined(andereDienst.functieAndereDienst),
      this.getTrimmedOrUndefined(andereDienst.naamDienstverlenerOfOrganisatieAndereDienst),
      this.getTrimmedOrUndefined(andereDienst.extraInfoAndereDienst),
    ))
      .filter(andereDienst => andereDienst.hasContent());
  }

  private createAlgemeneInformatie(): AlgemeneInformatie {
    return new AlgemeneInformatie(
      this.getTrimmedValueOrUndefined(this.dienstencheques) as DienstenChequesType,
      this.gebruikersnummer.value || undefined,
      this.geboortedatum.value?.format(dateFormat) || undefined,
      this.getTrimmedValueOrUndefined(this.moedertaal),
      this.getTrimmedValueOrUndefined(this.conversatieTaal),
      this.getTrimmedValueOrUndefined(this.keyLock),
      this.getTrimmedValueOrUndefined(this.autoStaanPlaats),
      this.getTrimmedValueOrUndefined(this.extraInfoBereikbaarheid),
      this.getTrimmedValueOrUndefined(this.inwonendeGezinsleden),
      this.getTrimmedValueOrUndefined(this.socialeSituatieSchets),
      this.getTrimmedValueOrUndefined(this.extraInformatie),
    );
  }

  private createTakenpakket(): Takenpakket {
    return new Takenpakket(
      this.getTrimmedValueOrUndefined(this.redenOpstart),
      this.getTrimmedValueOrUndefined(this.teBehalenDoelstellingen),
      this.createTakenPakketCategorie(this.poetsdienstStatus, this.poetsdienstFrequentie, this.poetsdienstVasteMedewerkers, this.poetsdienstBelangrijksteTaken, undefined),
      this.createTakenPakketCategorie(this.gezinszorgStatus, this.gezinszorgFrequentie, this.gezinszorgVasteMedewerkers, this.gezinszorgBelangrijksteTaken, this.gezinszorgSpecifiekeInformatieVoorWeekend),
      this.createTakenPakketCategorie(this.maaltijdenAanHuisStatus, this.maaltijdenAanHuisFrequentie, undefined, this.maaltijdenAanHuisBelangrijksteTaken, undefined),
      this.createTakenPakketCategorie(this.klusjesdienstStatus, undefined, undefined, this.klusjesdienstBelangrijksteTaken, undefined),
      this.createTakenPakketCategorie(this.psycholoogStatus, undefined, undefined, this.psycholoogBelangrijksteTaken, undefined),
      this.createTakenPakketCategorie(this.ergotherapeutStatus, undefined, undefined, this.ergotherapeutBelangrijksteTaken, undefined),
      this.createTakenPakketCategorie(this.mantelzorgcoordinatorStatus, undefined, undefined, this.mantelzorgcoordinatorBelangrijksteTaken, undefined),
      this.createTakenPakketCategorie(this.casemanagerStatus, undefined, undefined, this.casemanagerBelangrijksteTaken, undefined),
      this.createCasaClementina(),
      this.getTrimmedValueOrUndefined(this.stofzuigerWaarTeVinden),
      this.getTrimmedValueOrUndefined(this.poetsproductenWaarTeVinden),
      this.getTrimmedValueOrUndefined(this.doekenVoddenDweilenWaarTeVinden),
      this.getTrimmedValueOrUndefined(this.telescopischeSteelWaarTeVinden),
      this.getTrimmedValueOrUndefined(this.andereWaarTeVinden),
      this.getTrimmedValueOrUndefined(this.aandachtspuntenPoetsen),
      this.getTrimmedValueOrUndefined(this.aandachtspuntenAndereTaken),
    );
  }

  private createTakenPakketCategorie(status: AbstractControl, frequentie: AbstractControl | undefined, vasteMedewerkers: AbstractControl | undefined, belangrijksteTaken: AbstractControl, specifiekeInformatieVoorWeekend: AbstractControl | undefined) {
    return new TakenpakketCategorie(
      this.getTrimmedValueOrUndefined(status) as TakenpakketCategorieStatus,
      this.getTrimmedValueOrUndefined(frequentie),
      this.getTrimmedValueOrUndefined(vasteMedewerkers),
      this.getTrimmedValueOrUndefined(belangrijksteTaken),
      this.getTrimmedValueOrUndefined(specifiekeInformatieVoorWeekend));
  }

  private createCasaClementina(): CasaClementina {
    return new CasaClementina(
      this.casaClementinaFinancieleOpties.value?.filter((financieelOptie: FinancieleOptie) => !!financieelOptie),
      this.casaClementinaTransportOptiesNaarCasaClementina.value?.filter((transportOptie: TransportOptieNaarCasaClementina) => !!transportOptie),
      this.getTrimmedValueOrUndefined(this.casaClementinaVerduidelijkingAndereTransportOptieNaarCasaClementina),
      this.createContactpersoonNoodgeval(),
      this.getTrimmedValueOrUndefined(this.casaClementinaAandachtspuntenEnGemaakteAfspraken),
    );
  }

  private createContactpersoonNoodgeval(): ContactpersoonNoodgeval {
    return new ContactpersoonNoodgeval(
      this.getTrimmedValueOrUndefined(this.casaClementinaContactpersoonNoodgevalNaam),
      this.getTrimmedValueOrUndefined(this.casaClementinaContactpersoonNoodgevalRelatieTotHulpvrager),
      this.getTrimmedValueOrUndefined(this.casaClementinaContactpersoonNoodgevalTelefoonnummer),
    )
  }

  private createKnelpunten(): Knelpunten {
    return new Knelpunten(
      this.getTrimmedValueOrUndefined(this.allergieen),
      this.getTrimmedValueOrUndefined(this.huisdieren),
      this.getTrimmedValueOrUndefined(this.roken),
    );
  }

  private createMogelijkeProblematieken() {
    return new MogelijkeProblematieken(
      this.getTrimmedValueOrUndefined(this.fysiekeProblematieken),
      this.getTrimmedValueOrUndefined(this.mentaleProblematieken),
      this.getTrimmedValueOrUndefined(this.financieleProblematieken),
      this.getTrimmedValueOrUndefined(this.andereProblematieken),
      this.getTrimmedValueOrUndefined(this.handvatenOmMetClientOmTeGaan),
      this.getTrimmedValueOrUndefined(this.afsprakennota),
    );
  }

  private onAndereDienstenChanges(): void {
    this.andereDiensten.valueChanges.subscribe(updatedAndereDiensten => {
      const lastAndereDienstAdded = lodash.last(updatedAndereDiensten) as {
        functieAndereDienst,
        naamDienstverlenerOfOrganisatieAndereDienst,
        extraInfoAndereDienst,
      };
      if (this.getTrimmedOrUndefined(lastAndereDienstAdded.functieAndereDienst)
        || this.getTrimmedOrUndefined(lastAndereDienstAdded.naamDienstverlenerOfOrganisatieAndereDienst)
        || this.getTrimmedOrUndefined(lastAndereDienstAdded.extraInfoAndereDienst)) {
        this.andereDiensten.push(this.getEmptyClientFicheDienstForm());
      }
    });
  }

  private setFormState(client: Client): void {
    if (client?.isGearchiveerd()) {
      this.ClientFicheBeheerForm.disable();
    } else {
      this.ClientFicheBeheerForm.enable();
    }
  }

  private getInitialClientFicheGeboortedatum() {
    if (this.client?.getFiche()?.getAlgemeneInformatie()?.getGeboortedatum()) {
      return moment(this.client?.getFiche()?.getAlgemeneInformatie()?.getGeboortedatum(), dateFormat);
    } else {
      return null
    }
  }

  private getInitialClientFicheAndereDiensten() {
    if (this.client?.hasFiche()) {
      return this.client?.getFiche()?.getAndereDiensten()
        ?.map(clientFicheDienst =>
          new FormGroup({
            functieAndereDienst: new FormControl(clientFicheDienst.getFunctie()),
            naamDienstverlenerOfOrganisatieAndereDienst: new FormControl(clientFicheDienst.getNaamDienstverlenerOfOrganisatie()),
            extraInfoAndereDienst: new FormControl(clientFicheDienst.getExtraInfo()),
          })
        );
    } else {
      return []
    }
  }

  private getEmptyClientFicheDienstForm() {
    return new FormGroup({
      functieAndereDienst: new FormControl(),
      naamDienstverlenerOfOrganisatieAndereDienst: new FormControl(),
      extraInfoAndereDienst: new FormControl(),
    });
  }

  private getTrimmedValueOrUndefined(control: AbstractControl | undefined): string | undefined {
    return this.getTrimmedOrUndefined(control?.value);
  }

  private getTrimmedOrUndefined(value: string | undefined): string | undefined {
    return value?.trim() || undefined;
  }

  get dienstencheques(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.dienstencheques;
  }

  get gebruikersnummer(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.gebruikersnummer;
  }

  get geboortedatum(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.geboortedatum;
  }

  get moedertaal(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.moedertaal;
  }

  get conversatieTaal(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.conversatieTaal;
  }

  get keyLock(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.keyLock;
  }

  get autoStaanPlaats(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.autoStaanPlaats;
  }

  get extraInfoBereikbaarheid(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.extraInfoBereikbaarheid;
  }

  get inwonendeGezinsleden(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.inwonendeGezinsleden;
  }

  get socialeSituatieSchets(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.socialeSituatieSchets;
  }

  get extraInformatie(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.extraInformatie;
  }

  get redenOpstart(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.redenOpstart;
  }

  get teBehalenDoelstellingen(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.teBehalenDoelstellingen;
  }

  get poetsdienstStatus(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.poetsdienstStatus;
  }

  get poetsdienstFrequentie(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.poetsdienstFrequentie;
  }

  get poetsdienstVasteMedewerkers(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.poetsdienstVasteMedewerkers;
  }

  get poetsdienstBelangrijksteTaken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.poetsdienstBelangrijksteTaken;
  }

  get gezinszorgStatus(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.gezinszorgStatus;
  }

  get gezinszorgFrequentie(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.gezinszorgFrequentie;
  }

  get gezinszorgVasteMedewerkers(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.gezinszorgVasteMedewerkers;
  }

  get gezinszorgBelangrijksteTaken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.gezinszorgBelangrijksteTaken;
  }

  get gezinszorgSpecifiekeInformatieVoorWeekend(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.gezinszorgSpecifiekeInformatieVoorWeekend;
  }

  get maaltijdenAanHuisStatus(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.maaltijdenAanHuisStatus;
  }

  get maaltijdenAanHuisFrequentie(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.maaltijdenAanHuisFrequentie;
  }

  get maaltijdenAanHuisBelangrijksteTaken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.maaltijdenAanHuisBelangrijksteTaken;
  }

  get klusjesdienstStatus(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.klusjesdienstStatus;
  }

  get klusjesdienstBelangrijksteTaken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.klusjesdienstBelangrijksteTaken;
  }

  get psycholoogStatus(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.psycholoogStatus;
  }

  get psycholoogBelangrijksteTaken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.psycholoogBelangrijksteTaken;
  }

  get ergotherapeutStatus(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.ergotherapeutStatus;
  }

  get ergotherapeutBelangrijksteTaken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.ergotherapeutBelangrijksteTaken;
  }

  get mantelzorgcoordinatorStatus(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.mantelzorgcoordinatorStatus;
  }

  get mantelzorgcoordinatorBelangrijksteTaken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.mantelzorgcoordinatorBelangrijksteTaken;
  }

  get casemanagerStatus(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.casemanagerStatus;
  }

  get casemanagerBelangrijksteTaken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.casemanagerBelangrijksteTaken;
  }

  get casaClementinaContactpersoonNoodgevalNaam(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.casaClementinaContactpersoonNoodgevalNaam;
  }

  get casaClementinaContactpersoonNoodgevalRelatieTotHulpvrager(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.casaClementinaContactpersoonNoodgevalRelatieTotHulpvrager;
  }

  get casaClementinaContactpersoonNoodgevalTelefoonnummer(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.casaClementinaContactpersoonNoodgevalTelefoonnummer;
  }

  get casaClementinaAandachtspuntenEnGemaakteAfspraken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.casaClementinaAandachtspuntenEnGemaakteAfspraken;
  }

  get casaClementinaTransportOptiesNaarCasaClementina(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.casaClementinaTransportOptiesNaarCasaClementina;
  }

  get casaClementinaVerduidelijkingAndereTransportOptieNaarCasaClementina(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.casaClementinaVerduidelijkingAndereTransportOptieNaarCasaClementina;
  }

  get casaClementinaFinancieleOpties(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.casaClementinaFinancieleOpties;
  }

  get stofzuigerWaarTeVinden(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.stofzuigerWaarTeVinden;
  }

  get poetsproductenWaarTeVinden(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.poetsproductenWaarTeVinden;
  }

  get doekenVoddenDweilenWaarTeVinden(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.doekenVoddenDweilenWaarTeVinden;
  }

  get telescopischeSteelWaarTeVinden(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.telescopischeSteelWaarTeVinden;
  }

  get andereWaarTeVinden(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.andereWaarTeVinden;
  }

  get aandachtspuntenPoetsen(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.aandachtspuntenPoetsen;
  }

  get aandachtspuntenAndereTaken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.aandachtspuntenAndereTaken;
  }

  get allergieen(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.allergieen;
  }

  get huisdieren(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.huisdieren;
  }

  get roken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.roken;
  }

  get fysiekeProblematieken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.fysiekeProblematieken;
  }

  get mentaleProblematieken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.mentaleProblematieken;
  }

  get financieleProblematieken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.financieleProblematieken;
  }

  get andereProblematieken(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.andereProblematieken;
  }

  get handvatenOmMetClientOmTeGaan(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.handvatenOmMetClientOmTeGaan;
  }

  get afsprakennota(): AbstractControl {
    return this.ClientFicheBeheerForm.controls.afsprakennota;
  }

  get andereDiensten(): FormArray {
    return this.ClientFicheBeheerForm.controls.andereDiensten as FormArray;
  }
}
